import { getAuthenticatedUser } from 'components/auth'
import { getRESTParams, postRESTParams, REMBLE_API_URL, debounce } from '.'
import { queryStringFromObj } from '../helpers/Strings'
import { app } from '../../../config'

const BASE_PATH = `${REMBLE_API_URL}/users/events`

const EVENT_KEY_SOURCE = 'source'
const EVENT_KEY_CATEGORY = 'category'
const EVENT_KEY_ACTION = 'action'
const EVENT_KEY_VALUE = 'value'

// sources: www, app, api, or ios
const EVENT_SOURCE_APP = 'app'

const EVENT_CATEGORY_ASSESSMENT = 'assessment'
const EVENT_CATEGORY_ACCOUNT = 'account'
const EVENT_CATEGORY_ACCOUNT_CHANGE = 'account_change'
const EVENT_CATEGORY_ARTICLE = 'article'
const EVENT_CATEGORY_AFFIRMATION = 'affirmation'
const EVENT_CATEGORY_COMPLIMENTS = 'compliments'
const EVENT_CATEGORY_COURSE = 'course'
const EVENT_CATEGORY_CHAT = 'chat'
const EVENT_CATEGORY_DAILY_REMBLE = 'daily-remble'
const EVENT_CATEGORY_JOURNALING = 'journaling'
const EVENT_CATEGORY_COPING_SKILLS = 'coping-skills'
const EVENT_CATEGORY_LESSON = 'lesson'
const EVENT_CATEGORY_MEDITATION = 'meditation'
const EVENT_CATEGORY_MEMBERSHIP = 'membership'
const EVENT_CATEGORY_BREATHWORK = 'breathwork'
const EVENT_CATEGORY_PAYMENTS = 'payments'
const EVENT_CATEGORY_PROGRAM = 'program'
const EVENT_CATEGORY_PRACTICES = 'practices'
const EVENT_CATEGORY_REMINDERS = 'reminders'
const EVENT_CATEGORY_RELATIONSHIP_QUESTIONS = 'relationship_questions'
const EVENT_CATEGORY_DATE_IDEAS = 'date_ideas'
const EVENT_CATEGORY_QUIZ = 'quiz'
const EVENT_CATEGORY_TODAY = 'today'
const EVENT_CATEGORY_TOPIC = 'topic'
const EVENT_CATEGORY_QUESTION = 'question'
const EVENT_CATEGORY_VIDEO = 'video'
const EVENT_CATEGORY_WELCOME = 'welcome'
const EVENT_CATEGORY_SEARCH = 'search'
const EVENT_CATEGORY_SUBSCRIPTION = 'subscription'

const EVENT_ACTION_BACK = 'back'
const EVENT_ACTION_CANCEL = 'cancel'
const EVENT_ACTION_CHAT = 'chat'
const EVENT_ACTION_CONTINUE = 'continue'
const EVENT_ACTION_COMPLETE = 'complete'
const EVENT_ACTION_ENDED = 'ended'
const EVENT_ACTION_FILTER = 'filter'
const EVENT_ACTION_LOGIN = 'login'
const EVENT_ACTION_LOGOUT = 'logout'
const EVENT_ACTION_PAGEVIEW = 'page_view'
const EVENT_ACTION_DELETE = 'delete'
const EVENT_ACTION_SAVE = 'save'
const EVENT_ACTION_PAUSE = 'pause'
const EVENT_ACTION_PLAYING = 'playing'
const EVENT_ACTION_SUBMIT = 'submit'
const EVENT_ACTION_START = 'start'
const EVENT_ACTION_SEARCH = 'search'

const EVENT_VALUE_ALL = 'all'
const EVENT_VALUE_SUMMARY = 'summary'
const EVENT_VALUE_INTRO = 'intro'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common User Events GET request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See getRESTParams method for more info.
 */
const _userEventsRESTGET = async (url, params) => {
  const result = await fetch(url, getRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Common User Events POST request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _userEventsRESTPOST = async (url, params) =>
  _userEventsRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/**
 * Tries to post user event data to the Google Analytics API.
 * @param {String} category The event category (e.g., "affirmation", "journal", "lesson", "topic", etc.)
 * @param {String} action The event action (e.g., "click", "page_view", "complete", "submit", etc.)
 * @param {String} label The event label, if any (optional)
 * @param {String} value The event value, if any (optional)
 * @param {Number} conversionValue The conversion value, if any (optional)
 */
const trackGAEvent = async (
  category,
  action,
  label = null,
  value = null,
  conversionValue = 0
) => {
  // If we're in development mode, don't send to GA
  if (app.is_local) return

  // Try to send to GA if we have a GA tracker
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: action,
        category,
        label,
        value,
        conversionValue,
        page: {
          url: window.location.pathname,
          title: document.title
        }
      })
    }
  } catch (error) {
    console.error('error', error)
  }
}

/**
 * Tries to post user event data to the Intercom API.
 * @param {String} eventName The event name (e.g., "affirmation-page_view", "journal-submit", "lesson-complete", "topic-complete", etc.)
 * @param {Object} metadata The event metadata, if any (e.g., {"duration": "1:23", "ID": 123}, etc.)
 */
const trackIntercomEvent = async (eventName, metadata = {}) => {
  // If we're in development mode, don't send to GA
  if (app.is_local) return

  // Try to send to GA if we have a GA tracker
  try {
    if (window.Intercom) {
      window.Intercom('trackEvent', eventName, metadata)
    }
  } catch (error) {
    console.error('error', error)
  }
}

/**
 * Tries to post user event data to the Klaviyo API.
 * See https://developers.klaviyo.com/en/reference/track-identify-overview and https://developers.klaviyo.com/en/v1-2/docs/identify-api-reference for more info.
 * @param {String} category The event category (e.g., "affirmation", "journal", "lesson", "topic", etc.)
 * @param {String} action The event action (e.g., "click", "page_view", "complete", "submit", etc.)
 * @param {String} label The event label, if any (optional)
 * @param {String} value The event value, if any (optional)
 */
const _saveKlaviyoEvent = async (category, action, label = '', value = '') => {
  // If we're in development mode, don't send to GA
  if (app.is_local) return

  // Try to send to Klaviyo if we have a Klaviyo tracker
  try {
    const user = getAuthenticatedUser()
    const email = user?.email
    const name = user?.display_name
    const token = app.klaviyo.api_key

    // Don't send to Klaviyo if we don't have an email or key and return early
    if (!email || !token) return

    const isLogin =
      category === EVENT_CATEGORY_ACCOUNT && action === EVENT_ACTION_LOGIN

    let klaviyoUrl = 'https://a.klaviyo.com/api/track'
    let options = {
      method: 'POST',
      headers: {
        Accept: 'text/html',
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }

    // Track or Identify?
    if (isLogin) {
      // isLogin is true, so let's identify the user first
      klaviyoUrl = 'https://a.klaviyo.com/api/identify'
      options.body = new URLSearchParams({
        data: `{"token": "${token}","properties": {"$email":"${email}", "$first_name": "${name}", "host": "${app.host}"}}`
      })
    } else {
      options.body = new URLSearchParams({
        data: `{"token": "${token}", "event": "${category} ${action}", "customer_properties": {"$email": "${email}", "$first_name": "${name}"}, "properties": {"item_name": "${label}", "$value": "${value}", "host": "${app.host}"}}`
      })
    }

    // POST to Klaviyo
    fetch(klaviyoUrl, options)
      .then(response => response.json())
      .catch(err => console.error(err))
  } catch (error) {
    console.error('error', error)
  }
}

const identifyKlaviyoUser = async (user, subscription) => {
  // If we're in development mode, don't send to Klaviyo
  if (app.is_local) return

  // Try to send to Klaviyo if we have a Klaviyo tracker
  try {
    const email = user?.email
    const name = user?.display_name
    const token = app.klaviyo.api_key

    // Don't send to Klaviyo if we don't have an email or key and return early
    if (!email || !token) return

    const klaviyoUrl = 'https://a.klaviyo.com/api/identify'
    const options = {
      method: 'POST',
      headers: {
        Accept: 'text/html',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        data: `{"token": "${token}","properties": {"$email":"${email}", "$first_name": "${name}", "$plan_id": "${subscription.plan_id}"}}`
      })
    }

    // POST to Klaviyo
    fetch(klaviyoUrl, options)
      .then(response => response.json())
      .catch(err => console.error(err))
  } catch (error) {
    console.error('error', error)
  }
}

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Retrieves user events for the active user that match the passed in queryParam criteria or an empty array
 * @param {Object} queryParams Query parameters to pass to `/find` endpoint
 */
const getUserEvents = async ({
  category,
  action,
  source = EVENT_SOURCE_APP,
  limit = 10,
  distinct = true,
  exclude_categories = null
}) => {
  let url = `${BASE_PATH}/find`
  let queryObj = {
    source,
    limit
  }

  // Append the category to the payload if we have one
  if (category) queryObj['category'] = category

  // Append the action to the payload if we have one
  if (action) queryObj['action'] = action

  // Append the category exclusion to the payload if we have one
  if (exclude_categories) queryObj['exclude_categories'] = exclude_categories

  // Append the distinct to the payload if we have one
  if (distinct) queryObj['distinct'] = distinct

  url += queryStringFromObj(queryObj)

  const response = await fetch(url, getRESTParams(queryObj))
    .then(result => result.json())
    .catch(error => {
      console.error({ error })

      return error
    })

  return response.events ? response.events : []
}

/**
 * Tracks a user event to Meta's pixel
 * @param {String} name The event name (e.g., "ViewContent", etc.). See https://developers.facebook.com/docs/meta-pixel/reference for more info.
 * @param {Object} props The event properties and metadata. See https://developers.facebook.com/docs/meta-pixel/advanced/ for examples.
 * @returns Tracks a Marketing Event in the Facebook Pixel
 */
const trackMetaEvent = async (name, props) => {
  // If we're in development mode, don't send to Meta
  if (app.is_local) return

  try {
    window.fbq('track', name, props)
  } catch (error) {
    console.warn(error)
  } // Swallow exceptions
}

/**
 * Posts user event data to the /users/events/create endpoint to save user events
 * See https://remble.atlassian.net/browse/ENG-33 for more info
 * @param {String} category The event category (e.g., "affirmation", "journal", "lesson", "topic", etc.)
 * @param {String} action The event action (e.g., "click", "page_view", "complete", "submit", etc.)
 * @param {String} label The event label, if any (optional)
 * @param {String} value The event value, if any (optional)
 * @returns Returns an object with an error or an object with the post response data
 */
const saveUserEvent = async (category, action, label = null, value = null) => {
  const user = getAuthenticatedUser()

  // Don't save user events if we don't have a user
  if (!user) return

  const source = EVENT_SOURCE_APP
  const url = `${BASE_PATH}/create`
  let payload = {
    source,
    category,
    action
  }

  // Append a label to the payload if we have one
  if (label) payload['label'] = label

  // Append the value to the payload if we have one
  if (value) payload['value'] = value

  const body = JSON.stringify(payload)

  const saved = await _userEventsRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // ----------------------------------------
  // TODO: Move all tracking events to Tracking.js
  // ----------------------------------------

  // Save to GA if we have a GA tracker
  trackGAEvent(category, action, label, value)

  // Try to send to Facebook if we have a Facebook tracker
  trackMetaEvent('ViewContent', {
    content_name: `${category} ${action}`
  })

  // Try to send to Klaviyo if we have a Klaviyo tracker
  _saveKlaviyoEvent(category, action, label, value)

  // Try to send to Intercom if we have an Intercom tracker
  trackIntercomEvent(`${category}-${action}`, { label, value })

  return saved
}

/**
 * Simple debounce wrapper for the `saveUserEvent` function
 * See https://www.freecodecamp.org/news/javascript-debounce-example/ for more info
 * @param {String} category The event category (e.g., "affirmation", "journal", "lesson", "topic", etc.)
 * @param {String} action The event action (e.g., "click", "page_view", "complete", "submit", etc.)
 * @param {String} value The event value, if any (optional)
 * @param {*} timeout The debounce timeout threshold
 */
const saveUserEventDebounced = async (
  category,
  action,
  label = null,
  value = null,
  timeout = 500
) => {
  debounce(saveUserEvent(category, action, label, value), timeout)
}

export {
  getUserEvents,
  saveUserEvent,
  saveUserEventDebounced,
  trackGAEvent,
  trackMetaEvent,
  identifyKlaviyoUser,
  EVENT_ACTION_BACK,
  EVENT_ACTION_COMPLETE,
  EVENT_ACTION_CONTINUE,
  EVENT_ACTION_ENDED,
  EVENT_ACTION_FILTER,
  EVENT_ACTION_LOGIN,
  EVENT_ACTION_LOGOUT,
  EVENT_ACTION_PAGEVIEW,
  EVENT_ACTION_DELETE,
  EVENT_ACTION_SAVE,
  EVENT_ACTION_PAUSE,
  EVENT_ACTION_PLAYING,
  EVENT_ACTION_SUBMIT,
  EVENT_ACTION_CANCEL,
  EVENT_ACTION_START,
  EVENT_ACTION_CHAT,
  EVENT_ACTION_SEARCH,
  EVENT_CATEGORY_CHAT,
  EVENT_CATEGORY_ASSESSMENT,
  EVENT_CATEGORY_AFFIRMATION,
  EVENT_CATEGORY_COMPLIMENTS,
  EVENT_CATEGORY_ARTICLE,
  EVENT_CATEGORY_ACCOUNT,
  EVENT_CATEGORY_ACCOUNT_CHANGE,
  EVENT_CATEGORY_COURSE,
  EVENT_CATEGORY_DAILY_REMBLE,
  EVENT_CATEGORY_JOURNALING,
  EVENT_CATEGORY_COPING_SKILLS,
  EVENT_CATEGORY_LESSON,
  EVENT_CATEGORY_MEDITATION,
  EVENT_CATEGORY_MEMBERSHIP,
  EVENT_CATEGORY_BREATHWORK,
  EVENT_CATEGORY_SUBSCRIPTION,
  EVENT_CATEGORY_PAYMENTS,
  EVENT_CATEGORY_PRACTICES,
  EVENT_CATEGORY_REMINDERS,
  EVENT_CATEGORY_RELATIONSHIP_QUESTIONS,
  EVENT_CATEGORY_DATE_IDEAS,
  EVENT_CATEGORY_TODAY,
  EVENT_CATEGORY_TOPIC,
  EVENT_CATEGORY_QUIZ,
  EVENT_CATEGORY_QUESTION,
  EVENT_CATEGORY_VIDEO,
  EVENT_CATEGORY_WELCOME,
  EVENT_CATEGORY_PROGRAM,
  EVENT_CATEGORY_SEARCH,
  EVENT_KEY_SOURCE,
  EVENT_KEY_ACTION,
  EVENT_KEY_CATEGORY,
  EVENT_KEY_VALUE,
  EVENT_SOURCE_APP,
  EVENT_VALUE_ALL,
  EVENT_VALUE_SUMMARY,
  EVENT_VALUE_INTRO
}
